<template lang="pug">
  div
    #geoMap(:key="$store.state.global_map_key" :style="{height:`${height}px`}")
      v-progress-linear(v-if="loading" :indeterminate="loading")
      .statusGeoMap(v-if="status" v-html="status")
    v-dialog(v-model="showDialog" width="550" persistent)
      geofence-details(v-if="showDialog" @toggleDialog="toggleDialog" @save="save" @cancel="cancel" :key="$store.state.geofence.geofenceTableKey")

</template>

<script>
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import mapboxgl from "mapbox-gl";
import mapboxdraw from "@mapbox/mapbox-gl-draw";
import GeofenceDetails from "@/modules/maps/components/ui/Dialog/GeofenceDetails";
import {CircleMode, DragCircleMode, DirectMode, SimpleSelectMode} from "maplibre-gl-draw-circle"
import {extendDrawBar} from "@/modules/maps/components/maps/GeofenceControls";
import { point, polygon } from "@turf/helpers";
import centroid from "@turf/centroid";
import circle from "@turf/circle";
const turf = { centroid, circle, point, polygon };

// mapbox-gl-draw >=1.5 styles line-dasharray with a data expression, which
// mapbox-gl 2.x rejects; split that layer into active/inactive variants instead
const DRAW_STYLES = mapboxdraw.lib.theme.flatMap(layer => {
  if (layer.id !== "gl-draw-lines") return [layer];
  return [
    ["true", [0.2, 2], "#fbb03b"],
    ["false", [2, 0], "#3bb2d0"]
  ].map(([active, dash, color]) => ({
    ...layer,
    id: `gl-draw-lines-${active === "true" ? "active" : "inactive"}`,
    filter: ["all", ["==", "active", active], layer.filter],
    paint: {...layer.paint, "line-color": color, "line-dasharray": dash}
  }));
});

// each marker keeps a breadcrumb trail appended to on every AVL message;
// uncapped it grows for the whole session
const MAX_TRAIL_POINTS = 200;

export default {
  components: {
    GeofenceDetails,
  },

  props: {
    height: null
  },

  data: () => ({
    mapLoaded: false,
    loading: false,
    currentObject: null,
    status: null,
  }),

  mounted() {
    this.$store.commit("tracks/incrementMapKey");
    this.$store.geoMap = null;
    this.$store.geoDraw = null;

    if (this.$store.state.tracks.currentIndex != -1) {
      this.$nextTick(() => {
        this.createMap();
      })
    } else {
      this.$store.dispatch("tracks/loadAllTracks").then(() => {
        this.$nextTick(() => {
          this.createMap();
        });
      });
    }


  },

  beforeDestroy() {
    this.$nextTick(() => {
      if (this.$store.geoDraw != null) {
        delete (this.$store.geoDraw);
      }
      if (this.$store.geoMap != null) {
        this.$store.geoMap.remove();
      }

    })
  },

  watch: {
    avlDataList: function (avlArray) {
      if (avlArray.length > 0) {
        let avlData = avlArray[avlArray.length - 1];
        let tempMarker = this.markerList.find(e => e.vehicle_id == avlData.vehicle_id);
        if (tempMarker != null) {
          tempMarker.popUp.setHTML(
              `${tempMarker.popUpText}<span>Speed: ${avlData.speed}</span>`
          );
          tempMarker.setRotation(avlData.angle);
          tempMarker.setLngLat([avlData.location.lon, avlData.location.lat]);
          tempMarker.lineArray.push([
            avlData.location.lon,
            avlData.location.lat
          ]);
          if (tempMarker.lineArray.length > MAX_TRAIL_POINTS) {
            tempMarker.lineArray.splice(0, tempMarker.lineArray.length - MAX_TRAIL_POINTS);
          }
        }
      }
    }
  },

  computed: {
    items: function () {
      return this.$store.state.geofence.geofence_data
    },
    selectedGeofence: function () {
      return this.$store.state.geofence.currentIndex;
    },

    updateFlag: {
      get: function () {
        return this.$store.state.geofence.updateFlag;
      },
      set: function (value) {
        this.$store.commit("geofence/setUpdateFlag", value)
      },
    },

    createFlag: {
      get: function () {
        return this.$store.state.geofence.createFlag;
      },
      set: function (value) {
        this.$store.commit("geofence/setCreateFlag", value)
      }
    },

    avlDataList: function () {
      return this.$store.state.tracks.avl_data;
    },

    showDialog: {
      get: function () {
        return this.$store.state.geofence.showDialog;
      },
      set: function (value) {
        this.$store.commit("geofence/showDialog", value)
      }
    }
  },

  methods: {
    // create the map
    createMap: function () {
      mapboxgl.accessToken =
          "pk.eyJ1IjoibXVnYW1iaWtpbWF0aGkiLCJhIjoiY2p0OWNjaGZxMDdsdjQ0cXZjZTRhZnA0aSJ9.3b3qa7uHHInxWL3TBiMsSQ";
      this.$store.geoMap = new mapboxgl.Map({
        container: "geoMap",
        style: (this.$store.state.tracks.mapVectorStyle)?"mapbox://styles/mapbox/streets-v10":"mapbox://styles/mapbox/satellite-streets-v12",
        minzoom: 1.3,
        center: [36.8371433, -1.32593], // Nairobi
        zoom: 16,
        keyboard: false
      });
      const _canvas = this.$store.geoMap.getCanvas();
      _canvas.setAttribute('tabindex', '-1');
      _canvas.addEventListener('focus', (e) => {
        _canvas.blur();
        if (e.relatedTarget) e.relatedTarget.focus();
      });

      this.$store.geoDraw = new mapboxdraw({
        userProperties: true,
        displayControlsDefault: false,
        styles: DRAW_STYLES,

        modes: {
          ...mapboxdraw.modes,
          draw_circle: CircleMode,
          drag_circle: DragCircleMode,
          direct_select: DirectMode,
          simple_select: SimpleSelectMode
        }
      });

      var drawBar = new extendDrawBar({
        draw: this.$store.geoDraw,
        buttons: [
          {
            on: 'click',
            action: this.drawPolygon,
            classes: ['mapbox-gl-draw_polygon']
          },
          {
            on: 'click',
            action: this.drawCircle,
            classes: ['mapbox-gl-draw_point']
          },

          {
            on: 'click',
            action: this.deleteSelected,
            classes: ['mapbox-gl-draw_trash']
          },

        ]
      });
      this.$store.geoMap.addControl(drawBar);

      this.$store.geoMap.on("draw.create", this.createArea);
      this.$store.geoMap.on("draw.delete", this.deleteArea);
      this.$store.geoMap.on("draw.update", this.updateArea);
      this.$store.geoMap.on("draw.modechange", this.modeChange);
      this.$store.geoMap.on("draw.selectionchange", this.selectionChange);


      // ------------------------------ Track Points
      this.markerList = [];
      let trackPoints = []
      // loop for markerList moving Devices
      for (
          let i = 0;
          i < this.$store.getters["tracks/getMovingDevices"].length;
          i++
      ) {
        let trackDevice = this.$store.getters["tracks/getMovingDevices"][i];

        let el2 = document.createElement("div");
        el2.className = "markerDashboard";
        el2.setAttribute(
            "style",
            `background-image:url("/static/web/markers/marker_green_translucent.png")`
        );


        // create popup
        let tempPopUp = new mapboxgl.Popup(el2);
        let operator_name =
            trackDevice.contact != null
                ? `<br/>${trackDevice.contact.first_name} ${trackDevice.contact.last_name}`
                : ``;

        tempPopUp.setHTML(
            `<span style="font-weight: bold"> ${trackDevice.vehicle.name} </span>${operator_name}<br/><span>Speed: ${trackDevice.avl_record.speed}</span>`
        );
        tempPopUp.addTo(this.$store.geoMap);

        // add marker
        let tempMarker = new mapboxgl.Marker(el2);
        tempMarker.setLngLat([
          trackDevice.avl_record.location.lon,
          trackDevice.avl_record.location.lat
        ]);
        tempMarker.popUp = tempPopUp;
        tempMarker.popUpText = `<span style="font-weight: bold"> ${trackDevice.vehicle.name} </span>${operator_name}<br/>`;
        tempMarker.setPopup(tempPopUp);
        tempMarker.addTo(this.$store.geoMap);
        tempMarker.id = trackDevice.track_device.id;
        tempMarker.vehicle_id = trackDevice.vehicle.id;
        tempMarker.track_device = trackDevice.track_device;
        tempMarker.imei = trackDevice.track_device.imei;
        tempMarker.setRotation(trackDevice.track_device.angle);
        // add to cordinates path
        tempMarker.lineArray = [];
        tempMarker.lineArray.push([
          trackDevice.avl_record.location.lon,
          trackDevice.avl_record.location.lat
        ]);

        trackPoints.push({lon: trackDevice.avl_record.location.lon, lat: trackDevice.avl_record.location.lat})
        this.markerList.push(tempMarker);
      }


      // ------------------------------ End of Track points

      this.$store.geoMap.on("load", this.onLoadMap);

      this.$store.geoMap.on('moveend', () => this.$store.geoMap.getCanvas().blur());
      this.$store.geoMap.on('render', () => {
        if (document.activeElement === this.$store.geoMap.getCanvas()) {
          this.$store.geoMap.getCanvas().blur();
        }
      });
    },

    onLoadMap: function () {
      this.mapLoaded = true;

      const canvas = this.$store.geoMap.getCanvas();
      canvas.setAttribute('tabindex', '-1');
      canvas.addEventListener('focus', () => canvas.blur());
      // const allFeatures = this.$store.geoDraw.getAll().features
      // console.log(allFeatures)
      this.reDrawFeatures();

      // zoom to initial position
      this.zoomToInitial()

    },

    reDrawFeatures() {
      this.$store.geoDraw.changeMode(this.$store.geoDraw.modes.SIMPLE_SELECT)
      this.$store.geoDraw.deleteAll();

      this.items.forEach(element => {

        switch (element.geometry.type) {
          case "Polygon":
            var feature = {
              id: element.id,
              type: 'Feature',
              properties: {},
              geometry: element.geometry
            };
            this.$store.geoDraw.add(feature);
            this.$store.commit("geofence/updateObjectByGeofenceID", {id: element.id, feature: feature})
            break;
          case "Point":
            var circle = turf.circle(element.geometry.coordinates, element.radius);
            var feature2 = {
              id: element.id,
              type: 'Feature',
              properties: {
                isCircle: true,
                radiusInKm: element.radius,
                center: element.geometry.coordinates
              },
              geometry: circle.geometry
            };
            this.$store.geoDraw.add(feature2);
            this.$store.commit("geofence/updateObjectByGeofenceID", {id: element.id, feature: feature2})
            break;

        }

      })

      console.log("---- current Object ----");
      console.log(this.currentObject)
    },

    createArea: function (e) {
      console.log("create area");
      const current_mode = this.$store.geoDraw.getMode()
      console.log(current_mode)

      console.log(e.features[0])

      this.loading = true;
      this.status = false;
      this.$store.dispatch("geofence/addGeofence").then(() => {
        this.loading = false;
        this.currentObject = e.features[0]

        switch (current_mode) {
          case "draw_circle":
            var point = turf.point(this.currentObject.properties.center);
            this.$store.commit("geofence/updateGeofence", {path: "geometry", value: point.geometry});
            this.$store.commit("geofence/updateGeofence", {
              path: "radius",
              value: this.currentObject.properties.radiusInKm
            });
            break;
          case "draw_polygon":
            this.$store.commit("geofence/updateGeofence", {path: "geometry", value: this.currentObject.geometry});

            break;
        }


        this.$store.commit("geofence/updateGeofence", {path: "object", value: this.currentObject})
        this.toggleDialog();
      })

    },

    deleteArea: function (e) {
      console.log("delete area")
      console.log(e)
    },

    updateArea: function (e) {
      console.log("update Area")
      this.currentObject = e.features[0]
      this.updateFlag = true
    },


    selectionChange: function (e) {
      console.log("--------- selection change -------------")
      console.log(e)
      if (this.updateFlag) {
        this.loading = true
        this.$store.commit("geofence/updateObjectByObjectID", this.currentObject)
        this.$store.dispatch("geofence/saveGeofence").then(() => {
          this.currentObject = null;
          this.loading = false;
          this.updateFlag = false;
        })
      }

      // an element has been selected
      if (e.features.length == 1) {
        this.$store.commit("geofence/setCurrentIndexByObjectID", e.features[0].id)
        if(e.features[0].properties.isCircle != undefined && e.features[0].properties.isCircle == true){
          this.status = "<b>Drag the Object to move</b> or <b>Adjust <span style='color:#f35c00; font-size:14px'>radius</span> by clicking and dragging vertex points</b>"
        }else{
          console.log(this.$store.geoDraw.getMode())
          if(this.$store.geoDraw.getMode()=="direct_select"){
            this.status = "<ul><li><b>Adjust <span style='color:#f35c00; font-size:14px'>shape</span>by clicking and dragging vertex points</b></li><li><b>Add vertex by clicking and dragging <span style='color:#f35c00; font-size:14px'>middle</span>  vertex</b></li></ul>"
          }else{
            this.status = "<b>Drag the Object to move</b> <br/><b>Adjust <span style='color:#f35c00; font-size:14px'>shape</span>  by clicking and dragging vertex points</b>"
          }

        }


      }else{
        this.status = null;
      }

    },
    modeChange: function (e) {
      console.log("--------- mode change -------------")
      console.log(e)
    },

    drawCircle: function () {
      this.updateFlag = false;
      this.createFlag = true;
      this.$store.geoDraw.changeMode("draw_circle", {initialRadiusInKm: 0.2})
      this.status = "Click map to place <span style='color:#f35c00; font-size:14px'><b>Center-Point</b></span>"

    },

    drawPolygon: function () {
      this.updateFlag = false;
      this.createFlag = true;
      this.$store.geoDraw.changeMode("draw_polygon")
      this.status = "1. <span style='color:#f35c00; font-size:14px'><b>Click map</b></span> to place points <br/>2. <span style='color:#f35c00; font-size:14px'><b>Double Click</b></span> to Close"
    },

    deleteSelected: function () {

      if(this.$store.geoDraw.getSelectedIds().length == 0){
        this.status = "Select <b>Geofence</b> to Delete"
      }

      this.$store.geoDraw.getSelectedIds().forEach(element => {
        let index = this.$store.getters["geofence/getGeofenceByObjectIndexByObjectId"](element)

        if (index > -1) {
          // console.log(this.$store.state.geofence.geofence_data[index].id)
          this.$store.dispatch("geofence/deleteGeofence", this.$store.state.geofence.geofence_data[index].id).then(() => {
            this.$store.geoDraw.delete(element)
          })
        }

      })
    },

    toggleDialog: function () {
      this.showDialog = !this.showDialog;
      this.createFlag = false;
    },

    cancel: function () {
      if (this.createFlag) {
        this.$store.commit("geofence/deleteLast")
        this.$store.geoDraw.getSelectedIds().forEach(element => {
          this.$store.geoDraw.delete(element)
        })
      }

      this.$store.dispatch("geofence/loadAllGeofences");

      // update table
      this.$store.commit("geofence/incrementGeofenceTableKey");

      this.toggleDialog();
    },

    save: function () {
      // console.log("save data")

      this.showDialog = false;
      this.loading = true;
      console.log(this.$store.getters["geofence/getCurrentGeofence"])
      this.$store.dispatch("geofence/saveGeofence").then(() => {
        this.loading = false;
        this.createFlag = false;
        let currentGeofence = this.$store.getters["geofence/getCurrentGeofence"]
        if (currentGeofence.place_id != null) {

            let place = this.$store.getters["place/getPlaceByID"](currentGeofence.place_id)
          if (typeof place != "undefined"){
            this.$store.commit("place/setCurrentIndexByID",place.id)
            this.$store.commit("place/updatePlace", {path: "radius", value: currentGeofence.radius * 1000})
            this.$store.commit("place/updatePlace", {path: "location", value: {lon: currentGeofence.geometry.coordinates[0], lat: currentGeofence.geometry.coordinates[1]}})
            this.$store.commit("place/updatePlace", {path: "name", value: currentGeofence.name})
            this.$store.commit("place/updatePlace", {path: "description", value: currentGeofence.description})
          }
        }

        // update table
        this.$store.commit("geofence/incrementGeofenceTableKey");

      })
    },

    zoomToInitial: function () {
      // zoom to the current selection
      let item = this.$store.getters["geofence/getCurrentGeofence"]
      if (typeof item != 'undefined' && item.geometry != 'undefined') {


        switch (item.geometry.type) {
          case "Point":
            this.$store.geoMap.flyTo({
              center: [item.geometry.coordinates[0], item.geometry.coordinates[1]],
              essential: true
            })
            break;
          case "Polygon":
            console.log(item.geometry)
            var polygon = turf.polygon(item.geometry.coordinates);

            var centroid = turf.centroid(polygon)
            this.$store.geoMap.flyTo({
              center: [centroid.geometry.coordinates[0], centroid.geometry.coordinates[1]],
              essential: true
            })

        }
      } else {
        if (this.markerList.length > 0) {
          this.$store.geoMap.flyTo({
            center: this.markerList[0].getLngLat(),
            essential: true
          })
          ;
        }
      }
    }

  }
}
</script>

<style scoped lang="stylus">
.the-circle {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' /%3E%3C/svg%3E");
}

.statusGeoMap{
  position: absolute;
  bottom: 40px;
  left: 10px;
  z-index: 50;
  width: 40%;
  max-height: 100px;
  background-color: rgba(255, 255, 255, 0.65);
  padding: 5px 5px;
  border:2px solid;
  border-radius: 5px;
  border-color: #999999;
}

</style>